import type { Database, Json } from "@/lib/database.types";
import {
  buildProjectSourceHref,
} from "@/features/story/mappers/storyMappers";
import type { AiActionErrorBody } from "@/features/ai/aiErrors";
import type {
  AiContextCacheStatus,
  AiChatCitationView,
  AiFeatureAction,
  AiGuardNoticeView,
  AiGuardOutcomeView,
  ContinuationSuggestionMutationResult,
  ContinuationSuggestionSessionView,
  ContinuationUsageView,
  ContinuityIssueCitationView,
  ContinuityIssueResolutionStatus,
  ContinuityIssueSeverity,
  ContinuityIssueView,
  ContinuityReviewView,
  EnhanceSuggestionResult,
  PolishSuggestionResult,
  StructuralAnalysisCalloutReviewState,
  StructuralAnalysisCalloutView,
  StructuralAnalysisDimensionName,
  StructuralAnalysisHistoryEntryView,
  StructuralAnalysisMutationResult,
  StructuralAnalysisRunView,
  StructuralAnalysisScopeSnapshotView,
  StructuralAnalysisSectionView,
  StructuralAnalysisStatusMutationResult,
} from "@/features/ai/ai.types";
import { computeWordDiff } from "@/features/ai/textDiff";
import type { SubscriptionTier } from "@/types/projects";
import type { ChapterView } from "@/types/story";

type ContinuationSuggestionRow =
  Database["public"]["Tables"]["continuation_suggestions"]["Row"];
type ContinuityIssueRow = Database["public"]["Tables"]["continuity_issues"]["Row"];

type ContinuationSuggestionResponsePayload = {
  cacheStatus?: unknown;
  chapterId: string;
  createdAt: string;
  id: string;
  platformRestriction?: {
    resetAt?: unknown;
    stage?: unknown;
  } | null;
  suggestions: unknown;
  stage?: unknown;
  truncated?: boolean | null;
  usage: {
    creditCost?: number | null;
    creditsRemaining?: number | null;
    tier?: SubscriptionTier | null;
    usesRemainingToday?: number | null;
  };
};

type ContinuityIssuePayload = {
  citedSources?: unknown;
  description: string;
  id: string;
  resolution?: ContinuityIssueResolutionStatus | null;
  severity: ContinuityIssueSeverity;
};

type ContinuityReviewResponsePayload = {
  cacheStatus?: unknown;
  chapterId: string;
  creditsRemaining?: number | null;
  issues?: ContinuityIssuePayload[] | null;
  noCanonContext?: boolean | null;
  platformRestriction?: {
    resetAt?: unknown;
    stage?: unknown;
  } | null;
  retrievedCanonCount?: number | null;
  stage?: unknown;
  truncated?: boolean | null;
};

type StructuralFindingPayload = {
  chapter_id?: unknown;
  status?: unknown;
  text?: unknown;
};

type StructuralReportPayload = {
  dimensions?: Array<{
    findings?: unknown;
    name?: unknown;
  }> | null;
};

type StructuralScopeSnapshotPayload = {
  chapterCount?: unknown;
  chapterIds?: unknown;
  endChapterId?: unknown;
  scopeKind?: unknown;
  scopeLabelSnapshot?: unknown;
  startChapterId?: unknown;
};

type StructuralRunPayload = {
  chapterRange?: unknown;
  createdAt: string;
  creditsUsed: number;
  id: string;
  report?: unknown;
  storyId?: string | null;
};

type SourceCitationPayload = {
  evidenceNote?: string | null;
  sourceKind?: string | null;
  sourceName?: string | null;
  sourceRecordId?: string | null;
};

type SourceCitation = {
  evidenceNote: string | null;
  sourceKind: "chapter" | "lore_entry";
  sourceName: string;
  sourceRecordId: string | null;
};

const severityLabels: Record<ContinuityIssueSeverity, string> = {
  critical: "Critical",
  low: "Low",
  medium: "Medium",
};

const resolutionLabels: Record<ContinuityIssueResolutionStatus, string> = {
  acknowledged: "Acknowledged",
  dismissed: "Dismissed",
  open: "Open",
  resolved: "Resolved",
};

const structuralDimensionLabels: Record<StructuralAnalysisDimensionName, string> = {
  chapter_balance: "Chapter balance",
  character_voice_consistency: "Character voice consistency",
  pacing: "Pacing",
  structural_gaps: "Structural gaps",
  tension_arc: "Tension arc",
};

const structuralFindingStatusLabels: Record<StructuralAnalysisCalloutReviewState, string> = {
  acknowledged: "Acknowledged",
  dismissed: "Dismissed",
  open: "Open",
};

const structuralDimensionOrder: StructuralAnalysisDimensionName[] = [
  "pacing",
  "tension_arc",
  "chapter_balance",
  "character_voice_consistency",
  "structural_gaps",
];

function coerceAiContextCacheStatus(value: unknown): AiContextCacheStatus {
  if (value === "hit" || value === "miss" || value === "not-applicable") {
    return value;
  }

  if (value === "invalidated") {
    return "miss";
  }

  return "not-applicable";
}

function coerceGuardCacheStatus(value: unknown): AiActionErrorBody["cacheStatus"] {
  return coerceAiContextCacheStatus(value);
}

function coerceTimestamp(value: unknown) {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function coerceRetryAfterSeconds(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

function coerceResetMinutes(input: {
  resetAt?: unknown;
  retryAfterSeconds?: unknown;
}) {
  const retryAfterSeconds = coerceRetryAfterSeconds(input.retryAfterSeconds);

  if (retryAfterSeconds !== null) {
    return Math.max(1, Math.ceil(retryAfterSeconds / 60));
  }

  const resetAt = coerceTimestamp(input.resetAt);
  if (!resetAt) {
    return null;
  }

  const diffMs = new Date(resetAt).getTime() - Date.now();
  if (!Number.isFinite(diffMs) || diffMs <= 0) {
    return 1;
  }

  return Math.max(1, Math.ceil(diffMs / 60000));
}

function coerceStringArray(value: unknown) {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];
}

function coerceResolution(
  value: unknown,
): ContinuityIssueResolutionStatus {
  return value === "acknowledged"
    || value === "dismissed"
    || value === "resolved"
    || value === "open"
    ? value
    : "open";
}

function coerceSeverity(value: unknown): ContinuityIssueSeverity {
  return value === "critical" || value === "medium" || value === "low"
    ? value
    : "medium";
}

function coerceStructuralDimensionName(value: unknown): StructuralAnalysisDimensionName | null {
  return value === "pacing"
    || value === "tension_arc"
    || value === "chapter_balance"
    || value === "character_voice_consistency"
    || value === "structural_gaps"
    ? value
    : null;
}

function coerceStructuralFindingStatus(
  value: unknown,
): StructuralAnalysisCalloutReviewState {
  return value === "acknowledged" || value === "dismissed" || value === "open"
    ? value
    : "open";
}

function clipPreviewText(text: string, maxChars = 120) {
  const normalized = text.replace(/\s+/g, " ").trim();

  if (normalized.length <= maxChars) {
    return normalized;
  }

  return `${normalized.slice(0, Math.max(0, maxChars - 3)).trimEnd()}...`;
}

function normalizeSourceCitation(input: unknown): SourceCitation | null {
  if (!input || typeof input !== "object") {
    return null;
  }

  const citation = input as SourceCitationPayload;
  const sourceKind =
    citation.sourceKind === "chapter" || citation.sourceKind === "lore_entry"
      ? citation.sourceKind
      : null;
  const sourceName =
    typeof citation.sourceName === "string" && citation.sourceName.trim().length > 0
      ? citation.sourceName.trim()
      : null;

  if (!sourceKind || !sourceName) {
    return null;
  }

  return {
    evidenceNote:
      typeof citation.evidenceNote === "string" && citation.evidenceNote.trim().length > 0
        ? citation.evidenceNote.trim()
        : null,
    sourceKind,
    sourceName,
    sourceRecordId:
      typeof citation.sourceRecordId === "string" && citation.sourceRecordId.trim().length > 0
        ? citation.sourceRecordId
        : null,
  };
}

function mapAiChatCitation(
  projectId: string,
  citation: ReturnType<typeof normalizeSourceCitation>,
  index: number,
): AiChatCitationView | null {
  if (!citation) {
    return null;
  }

  return {
    evidenceNote: citation.evidenceNote,
    openHref: buildProjectSourceHref(projectId, citation.sourceKind, citation.sourceRecordId),
    sourceKind: citation.sourceKind,
    sourceName: citation.sourceName,
    sourceRecordId: citation.sourceRecordId ?? `${citation.sourceKind}-${index + 1}`,
  };
}

function mapContinuityCitation(
  projectId: string,
  citation: ReturnType<typeof normalizeSourceCitation>,
  index: number,
): ContinuityIssueCitationView | null {
  if (!citation) {
    return null;
  }

  return {
    canonStatusLabel: citation.sourceKind === "lore_entry" ? "Canon" : null,
    evidenceSummary: citation.evidenceNote ?? "Saved citation snapshot",
    id: citation.sourceRecordId ?? `${citation.sourceKind}-${index + 1}`,
    openHref: buildProjectSourceHref(projectId, citation.sourceKind, citation.sourceRecordId),
    sourceKind: citation.sourceKind,
    sourceNameSnapshot: citation.sourceName,
    sourceRecordId: citation.sourceRecordId,
  };
}

export function buildCanonSearchQuery(text: string, maxTerms = 8) {
  const words = text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .map((word) => word.trim())
    .filter((word) => word.length >= 4);

  const seen = new Set<string>();
  const terms: string[] = [];

  for (const word of words) {
    if (seen.has(word)) {
      continue;
    }

    seen.add(word);
    terms.push(word);

    if (terms.length >= maxTerms) {
      break;
    }
  }

  return terms.join(" ");
}

export function mapContinuationSuggestionRowToView(
  row: Pick<ContinuationSuggestionRow, "chapter_id" | "created_at" | "id" | "suggestions">,
): ContinuationSuggestionSessionView {
  const suggestions = coerceStringArray(row.suggestions);

  return {
    chapterId: row.chapter_id,
    createdAt: row.created_at,
    id: row.id,
    options: suggestions.map((text, index) => ({
      id: `${row.id}-${index + 1}`,
      text,
    })),
  };
}

export function mapContinuationSuggestionRows(
  rows: Array<Pick<ContinuationSuggestionRow, "chapter_id" | "created_at" | "id" | "suggestions">>,
) {
  return rows.map(mapContinuationSuggestionRowToView);
}

export function mapContinuationUsage(input: {
  creditCost?: number | null;
  creditsRemaining?: number | null;
  tier: SubscriptionTier;
  usesRemainingToday?: number | null;
}): ContinuationUsageView {
  return {
    creditCost: typeof input.creditCost === "number" ? input.creditCost : 0,
    creditsRemaining:
      typeof input.creditsRemaining === "number" ? input.creditsRemaining : null,
    tier: input.tier,
    usesRemainingToday:
      typeof input.usesRemainingToday === "number" ? input.usesRemainingToday : null,
  };
}

export function mapContinuationSuggestionResponse(
  payload: ContinuationSuggestionResponsePayload,
): ContinuationSuggestionMutationResult {
  const platformStage = typeof payload.platformRestriction?.stage === "string"
    ? payload.platformRestriction.stage
    : payload.stage;
  const platformResetAt = typeof payload.platformRestriction?.resetAt === "string"
    ? payload.platformRestriction.resetAt
    : null;

  return {
    guardOutcome: mapAiGuardOutcome({
      cacheStatus: coerceGuardCacheStatus(payload.cacheStatus),
      feature: "continuation",
      resetAt: platformResetAt,
      stage: platformStage as AiActionErrorBody["stage"],
      creditsSafe: platformStage === "ai_restricted_100",
      truncated: payload.truncated === true,
    }),
    session: mapContinuationSuggestionRowToView({
      chapter_id: payload.chapterId,
      created_at: payload.createdAt,
      id: payload.id,
      suggestions: payload.suggestions as Json,
    }),
    usage: mapContinuationUsage({
      creditCost: payload.usage.creditCost,
      creditsRemaining: payload.usage.creditsRemaining,
      tier: payload.usage.tier ?? "free",
      usesRemainingToday: payload.usage.usesRemainingToday,
    }),
  };
}

export function mapContinuityIssueToView(
  projectId: string,
  issue: Pick<ContinuityIssueRow, "cited_sources" | "description" | "id" | "resolution" | "severity">,
): ContinuityIssueView {
  const severity = coerceSeverity(issue.severity);
  const resolutionStatus = coerceResolution(issue.resolution);
  const citations = Array.isArray(issue.cited_sources)
    ? issue.cited_sources
        .map(normalizeSourceCitation)
        .map((citation, index) => mapContinuityCitation(projectId, citation, index))
        .filter((citation): citation is ContinuityIssueCitationView => Boolean(citation))
    : [];

  return {
    citations,
    description: issue.description,
    id: issue.id,
    resolutionStatus,
    resolutionStatusLabel: resolutionLabels[resolutionStatus],
    severity,
    severityLabel: severityLabels[severity],
  };
}

export function mapContinuityIssueRows(projectId: string, rows: ContinuityIssueRow[]) {
  return rows.map((row) => mapContinuityIssueToView(projectId, row));
}

export function mapContinuityReviewResponse(
  projectId: string,
  payload: ContinuityReviewResponsePayload,
): ContinuityReviewView {
  const platformStage = typeof payload.platformRestriction?.stage === "string"
    ? payload.platformRestriction.stage
    : payload.stage;
  const platformResetAt = typeof payload.platformRestriction?.resetAt === "string"
    ? payload.platformRestriction.resetAt
    : null;
  const issues = (payload.issues ?? []).map((issue) =>
    mapContinuityIssueToView(projectId, {
      cited_sources: (issue.citedSources ?? []) as Json,
      description: issue.description,
      id: issue.id,
      resolution: issue.resolution ?? "open",
      severity: issue.severity,
    })
  );
  const retrievedCanonCount = payload.retrievedCanonCount ?? 0;
  const noCanonContext = Boolean(payload.noCanonContext);

  return {
    chapterId: payload.chapterId,
    creditsRemaining:
      typeof payload.creditsRemaining === "number" ? payload.creditsRemaining : null,
    guardOutcome: mapAiGuardOutcome({
      cacheStatus: coerceGuardCacheStatus(payload.cacheStatus),
      creditsSafe: platformStage === "ai_restricted_100",
      feature: "continuity",
      resetAt: platformResetAt,
      stage: platformStage as AiActionErrorBody["stage"],
      truncated: payload.truncated === true,
    }),
    issues,
    noCanonContext,
    retrievedCanonCount,
    reviewSummary: noCanonContext
      ? "No canon evidence was available for this review."
      : issues.length === 0
        ? `Reviewed against ${retrievedCanonCount} canon sources with no stored issues.`
        : `Stored ${issues.length} continuity issues from ${retrievedCanonCount} canon sources.`,
  };
}

export function mapAiChatCitations(projectId: string, input: unknown) {
  return Array.isArray(input)
    ? input
        .map(normalizeSourceCitation)
        .map((citation, index) => mapAiChatCitation(projectId, citation, index))
        .filter((citation): citation is AiChatCitationView => Boolean(citation))
    : [];
}

export function buildAiGuardNotices(input: {
  cacheStatus?: unknown;
  code?: AiActionErrorBody["code"];
  creditsSafe?: AiActionErrorBody["creditsSafe"];
  feature?: AiFeatureAction | null;
  resetAt?: AiActionErrorBody["resetAt"];
  retryAfterSeconds?: AiActionErrorBody["retryAfterSeconds"];
  stage?: AiActionErrorBody["stage"];
  truncated?: AiActionErrorBody["truncated"];
}): AiGuardNoticeView[] {
  const notices: AiGuardNoticeView[] = [];
  const feature = input.feature ?? null;
  const stage = typeof input.stage === "string" ? input.stage : null;
  const effectiveCode = input.code
    ?? (stage === "ai_restricted_100"
      ? "AI_BUDGET_RESTRICTED"
      : undefined);
  const detailKey = input.creditsSafe === true ? "story.ai.access.creditsSafe" : null;
  const resetAt = coerceTimestamp(input.resetAt);
  const retryAfterSeconds = coerceRetryAfterSeconds(input.retryAfterSeconds);
  const minutesUntilReset = coerceResetMinutes({
    resetAt,
    retryAfterSeconds,
  });

  if (input.truncated === true) {
    notices.push({
      detailKey: null,
      feature,
      kind: "truncation",
      messageKey: "story.ai.access.truncationWarning",
    });
  }

  if (effectiveCode === "AI_RATE_LIMIT_REACHED") {
    notices.push({
      creditsSafe: input.creditsSafe === true,
      detailKey,
      feature,
      kind: "rate-limit",
      messageKey: "story.ai.access.rateLimitPrompt",
      minutesUntilReset,
      resetAt,
      retryAfterSeconds,
    });
  }

  if (effectiveCode === "AI_BUDGET_RESTRICTED" || effectiveCode === "AI_IMAGE_BUDGET_RESTRICTED") {
    notices.push({
      creditsSafe: input.creditsSafe === true,
      detailKey,
      feature,
      kind: "budget-restricted",
      messageKey: "story.ai.access.budgetRestrictedPrompt",
      resetAt,
    });
  }

  if (effectiveCode === "AI_SERVICE_BUSY") {
    notices.push({
      creditsSafe: input.creditsSafe === true,
      detailKey,
      feature,
      kind: "service-busy",
      messageKey: "story.ai.access.serviceBusyPrompt",
      retryAfterSeconds,
    });
  }

  return notices;
}

export function mapAiGuardOutcome(input: {
  cacheStatus?: unknown;
  code?: AiActionErrorBody["code"];
  creditsSafe?: AiActionErrorBody["creditsSafe"];
  feature?: AiFeatureAction | null;
  resetAt?: AiActionErrorBody["resetAt"];
  retryAfterSeconds?: AiActionErrorBody["retryAfterSeconds"];
  stage?: AiActionErrorBody["stage"];
  truncated?: AiActionErrorBody["truncated"];
}): AiGuardOutcomeView {
  const cacheStatus = coerceAiContextCacheStatus(input.cacheStatus);
  const resetAt = coerceTimestamp(input.resetAt);
  const retryAfterSeconds = coerceRetryAfterSeconds(input.retryAfterSeconds);
  const stage = typeof input.stage === "string" && input.stage.trim().length > 0
    ? input.stage
    : null;

  return {
    cacheStatus,
    creditsSafe: input.creditsSafe === true,
    notices: buildAiGuardNotices(input),
    resetAt,
    retryAfterSeconds,
    stage,
    truncated: input.truncated === true,
  };
}

function coerceStructuralScopeSnapshot(
  input: unknown,
): StructuralAnalysisScopeSnapshotView {
  const snapshot = input && typeof input === "object"
    ? input as StructuralScopeSnapshotPayload
    : {};
  const chapterIds = Array.isArray(snapshot.chapterIds)
    ? snapshot.chapterIds.filter((value): value is string => typeof value === "string")
    : [];

  return {
    chapterCount:
      typeof snapshot.chapterCount === "number"
        ? snapshot.chapterCount
        : chapterIds.length,
    chapterIds,
    endChapterId:
      typeof snapshot.endChapterId === "string" ? snapshot.endChapterId : null,
    scopeKind:
      snapshot.scopeKind === "chapter-range" ? "chapter-range" : "full-story",
    scopeLabelSnapshot:
      typeof snapshot.scopeLabelSnapshot === "string" && snapshot.scopeLabelSnapshot.trim().length > 0
        ? snapshot.scopeLabelSnapshot
        : chapterIds.length <= 1
          ? "All chapters (1 chapter)"
          : `All chapters (${chapterIds.length} chapters)`,
    startChapterId:
      typeof snapshot.startChapterId === "string" ? snapshot.startChapterId : null,
  };
}

function mapStructuralFinding(
  projectId: string,
  dimensionName: StructuralAnalysisDimensionName,
  finding: StructuralFindingPayload,
  index: number,
  runId: string,
  chapterLookup: Map<string, ChapterView>,
): StructuralAnalysisCalloutView | null {
  const chapterId = typeof finding.chapter_id === "string" ? finding.chapter_id : null;
  const text =
    typeof finding.text === "string" && finding.text.trim().length > 0
      ? finding.text.trim()
      : null;

  if (!chapterId || !text) {
    return null;
  }

  const chapter = chapterLookup.get(chapterId);

  return {
    chapterId,
    chapterHref: chapter ? buildProjectSourceHref(projectId, "chapter", chapter.id) : null,
    chapterLabel: chapter
      ? `Chapter ${chapter.sortOrder + 1}: ${chapter.title}`
      : "Referenced chapter unavailable",
    dimension: dimensionName,
    dimensionLabel: structuralDimensionLabels[dimensionName],
    findingIndex: index,
    id: `${runId}:${dimensionName}:${index}`,
    reviewState: coerceStructuralFindingStatus(finding.status),
    reviewStateLabel: structuralFindingStatusLabels[coerceStructuralFindingStatus(finding.status)],
    runId,
    text,
  };
}

function mapStructuralSections(
  projectId: string,
  report: unknown,
  runId: string,
  chapterLookup: Map<string, ChapterView>,
): StructuralAnalysisSectionView[] {
  const rawDimensions = Array.isArray((report as StructuralReportPayload | null)?.dimensions)
    ? (report as StructuralReportPayload).dimensions ?? []
    : [];
  const dimensionMap = new Map(
    rawDimensions
      .map((dimension) => {
        const name = coerceStructuralDimensionName(dimension?.name);
        return name ? [name, dimension] as const : null;
      })
      .filter((entry): entry is readonly [StructuralAnalysisDimensionName, NonNullable<StructuralReportPayload["dimensions"]>[number]] => Boolean(entry)),
  );

  return structuralDimensionOrder.map((dimensionName) => {
    const dimension = dimensionMap.get(dimensionName);
    const findings = Array.isArray(dimension?.findings)
      ? dimension.findings
          .map((finding, index) =>
            mapStructuralFinding(
              projectId,
              dimensionName,
              finding as StructuralFindingPayload,
              index,
              runId,
              chapterLookup,
            ))
          .filter((finding): finding is StructuralAnalysisCalloutView => Boolean(finding))
      : [];

    return {
      dimension: dimensionName,
      dimensionLabel: structuralDimensionLabels[dimensionName],
      emptyStateLabel: "No chapter-linked findings were stored for this dimension.",
      findings,
      id: `${runId}:${dimensionName}`,
    };
  });
}

export function mapStructuralAnalysisRun(
  projectId: string,
  chapters: ChapterView[],
  payload: StructuralRunPayload,
): StructuralAnalysisRunView {
  const chapterLookup = new Map(chapters.map((chapter) => [chapter.id, chapter]));
  const scope = coerceStructuralScopeSnapshot(payload.chapterRange);
  const sections = mapStructuralSections(projectId, payload.report, payload.id, chapterLookup);

  return {
    chapterCount: scope.chapterCount,
    createdAt: payload.createdAt,
    creditsUsed: payload.creditsUsed,
    id: payload.id,
    scope,
    scopeLabelSnapshot: scope.scopeLabelSnapshot,
    sections,
    storyId: payload.storyId ?? null,
  };
}

export function mapStructuralAnalysisRows(
  projectId: string,
  chapters: ChapterView[],
  rows: Array<{
    chapter_range: unknown;
    created_at: string;
    credits_used: number;
    id: string;
    report: unknown;
    story_id: string | null;
  }>,
) {
  return rows.map((row) =>
    mapStructuralAnalysisRun(projectId, chapters, {
      chapterRange: row.chapter_range,
      createdAt: row.created_at,
      creditsUsed: row.credits_used,
      id: row.id,
      report: row.report,
      storyId: row.story_id,
    })
  );
}

export function mapStructuralAnalysisHistoryEntries(
  runs: StructuralAnalysisRunView[],
): StructuralAnalysisHistoryEntryView[] {
  return runs.map((run) => {
    const previewText = clipPreviewText(
      run.sections.flatMap((section) => section.findings).at(0)?.text
        ?? "Saved structural report covering pacing, tension, balance, voice, and structural gaps.",
    );

    return {
      chapterCount: run.chapterCount,
      createdAt: run.createdAt,
      creditsUsed: run.creditsUsed,
      previewText,
      runId: run.id,
      scopeLabel: run.scopeLabelSnapshot,
    };
  });
}

export function mapStructuralAnalysisResponse(
  projectId: string,
  chapters: ChapterView[],
  payload: {
    cacheStatus?: unknown;
    creditsRemaining?: number | null;
    platformRestriction?: {
      resetAt?: unknown;
      stage?: unknown;
    } | null;
    run: StructuralRunPayload;
    stage?: unknown;
    truncated?: boolean | null;
  },
): StructuralAnalysisMutationResult {
  const platformStage = typeof payload.platformRestriction?.stage === "string"
    ? payload.platformRestriction.stage
    : payload.stage;
  const platformResetAt = typeof payload.platformRestriction?.resetAt === "string"
    ? payload.platformRestriction.resetAt
    : null;

  return {
    creditsRemaining:
      typeof payload.creditsRemaining === "number" ? payload.creditsRemaining : null,
    guardOutcome: mapAiGuardOutcome({
      cacheStatus: coerceGuardCacheStatus(payload.cacheStatus),
      creditsSafe: platformStage === "ai_restricted_100",
      feature: "structural",
      resetAt: platformResetAt,
      stage: platformStage as AiActionErrorBody["stage"],
      truncated: payload.truncated === true,
    }),
    run: mapStructuralAnalysisRun(projectId, chapters, payload.run),
  };
}

export function mapStructuralAnalysisStatusResponse(payload: {
  dimensionName: StructuralAnalysisDimensionName;
  findingIndex: number;
  runId: string;
  status: StructuralAnalysisCalloutReviewState;
}): StructuralAnalysisStatusMutationResult {
  return payload;
}

function coerceEnhanceSourceContext(value: unknown): EnhanceSuggestionResult["sourceContext"] {
  if (!value || typeof value !== "object") {
    return null;
  }

  const context = value as Record<string, unknown>;
  return {
    chapterTitle: typeof context.chapterTitle === "string" ? context.chapterTitle : "",
    genre: typeof context.genre === "string" ? context.genre : null,
    language: typeof context.language === "string" ? context.language : null,
    styleGuideLoreEntryId:
      typeof context.styleGuideLoreEntryId === "string"
        ? context.styleGuideLoreEntryId
        : null,
    styleGuideUsed: context.styleGuideUsed === true,
    truncated: context.truncated === true,
  };
}

function coercePolishSourceContext(value: unknown): PolishSuggestionResult["sourceContext"] {
  if (!value || typeof value !== "object") {
    return null;
  }

  const context = value as Record<string, unknown>;
  return {
    chapterTitle: typeof context.chapterTitle === "string" ? context.chapterTitle : "",
    includedWordCount:
      typeof context.includedWordCount === "number" ? context.includedWordCount : null,
    language: typeof context.language === "string" ? context.language : null,
    originalWordCount:
      typeof context.originalWordCount === "number" ? context.originalWordCount : null,
    truncated: context.truncated === true,
  };
}

export function mapEnhanceSelectionResponse(payload: {
  chapterId: string;
  creditCost?: number | null;
  creditsRemaining?: number | null;
  noImprovements?: boolean | null;
  sourceContext?: unknown;
  suggestion?: string | null;
}): EnhanceSuggestionResult {
  return {
    chapterId: payload.chapterId,
    creditCost: typeof payload.creditCost === "number" ? payload.creditCost : 0,
    creditsRemaining:
      typeof payload.creditsRemaining === "number" ? payload.creditsRemaining : null,
    noImprovements: payload.noImprovements === true,
    sourceContext: coerceEnhanceSourceContext(payload.sourceContext),
    suggestion: typeof payload.suggestion === "string" ? payload.suggestion : null,
  };
}

export function mapPolishChapterResponse(payload: {
  chapterId: string;
  creditCost?: number | null;
  creditsRemaining?: number | null;
  noImprovements?: boolean | null;
  originalText?: string | null;
  sourceContext?: unknown;
  suggestion?: string | null;
}): PolishSuggestionResult {
  const originalText = typeof payload.originalText === "string" ? payload.originalText : null;
  const suggestion = typeof payload.suggestion === "string" ? payload.suggestion : null;

  return {
    chapterId: payload.chapterId,
    creditCost: typeof payload.creditCost === "number" ? payload.creditCost : 0,
    creditsRemaining:
      typeof payload.creditsRemaining === "number" ? payload.creditsRemaining : null,
    diff: originalText && suggestion ? computeWordDiff(originalText, suggestion) : null,
    noImprovements: payload.noImprovements === true,
    originalText,
    sourceContext: coercePolishSourceContext(payload.sourceContext),
    suggestion,
  };
}
